import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { seo } from "@/utils/seo";
import {
  Link,
  createFileRoute,
  redirect,
  useParams,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ChevronRight,
  Clock,
  Eye,
  MessageSquare,
  MoreHorizontal,
  Share2,
  User,
} from "lucide-react";

import { getAllMessagesOptions } from "@/actions/messages/handlers";
import { getSelectedTagOptions } from "@/actions/tags/handler";
import { useSuspenseQuery } from "@tanstack/react-query";

import MessageArea from "@/components/messageArea";
import ReplyEditor from "@/components/replyEditor";
import z from "zod";

export const Route = createFileRoute("/_app/forum/$tagId")({
  component: ForumThreadPage,
  loader: async ({ params, context }) => {
    const { tagId } = params;
    if (!z.cuid2().safeParse(tagId).success || !tagId) {
      throw redirect({ to: "/forum" });
    }
    const { body: tagData } = await context.queryClient.ensureQueryData({
      ...getSelectedTagOptions({ tagId }),
      retry: 2,
    });

    const { body: messagesData } = await context.queryClient.ensureQueryData({
      ...getAllMessagesOptions({ tagId }),
      retry: 2,
    });

    if (!tagData.tag) {
      throw redirect({ to: "/forum" });
    }

    return {
      tag: tagData,
      messages: messagesData.messages,
    };
  },
  head: ({ loaderData }) => {
    const tag = loaderData?.tag?.tag;
    const messages = loaderData?.messages;
    const title = tag?.tagTitle
      ? `${tag.tagTitle} | Forum | theCoach`
      : "Forum | theCoach";
    const description = tag?.tagContent
      ? tag.tagContent.slice(0, 160).trim() +
        (tag.tagContent.length > 160 ? "..." : "")
      : "theCoach forumunda konuları keşfedin, sorular sorun ve toplulukla etkileşime geçin.";

    return {
      meta: seo({
        title,
        description,
        keywords: `${tag?.tagTitle || ""}, ${tag?.category || ""}, powerlifting, fitness, forum`,
        canonical: `https://atalaythecoach.com/forum/${tag?.tagId}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DiscussionForumPosting",
            headline: tag?.tagTitle,
            articleBody: tag?.tagContent,
            author: {
              "@type": "Person",
              name: tag?.creater?.userName || "Anonim",
            },
            datePublished: tag?.createdAt
              ? new Date(tag.createdAt).toISOString()
              : undefined,
            interactionStatistic: [
              {
                "@type": "InteractionCounter",
                interactionType: "https://schema.org/CommentAction",
                userInteractionCount: messages?.messages.length || 0,
              },
            ],
          }),
        },
      ],
    };
  },
  errorComponent: () => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background pt-28 text-center text-foreground">
      <h1 className="font-bold font-serif text-4xl">Bir Hata Oluştu</h1>
      <p className="mt-4 text-muted-foreground">
        Konu yüklenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.
      </p>
      <Button asChild className="mt-6">
        <Link to="/forum">Forum Anasayfasına Dön</Link>
      </Button>
    </div>
  ),
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col items-center justify-center pt-28 text-center">
      <h1 className="font-bold font-serif text-4xl">Konu Bulunamadı</h1>
      <p className="mt-4 text-muted-foreground">
        Aradığınız konu mevcut değil veya kaldırılmış olabilir.
      </p>
      <Button className="mt-6">
        <Link to="/forum">Tüm Konulara Geri Dön</Link>
      </Button>
    </div>
  ),
});

function ForumThreadPage() {
  const { tagId } = useParams({ from: "/_app/forum/$tagId" });
  const { user } = useRouteContext({ from: "/_app" });
  const {
    data: { body: messageData },
  } = useSuspenseQuery(getAllMessagesOptions({ tagId }));
  const { data: post } = useSuspenseQuery(getSelectedTagOptions({ tagId }));
  const messages = messageData.messages.messages;

  return (
    <div className="min-h-screen bg-background pt-28 pb-16 text-foreground sm:pt-32">
      <div className="container mx-auto max-w-5xl px-4 ">
        <nav className="mb-4 flex items-center space-x-2 text-muted-foreground text-sm">
          <Link to="/forum" className="hover:text-primary">
            Forum
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-semibold text-foreground">
            {post?.body.tag?.category}
          </span>
        </nav>

        <article className="wrap-break-word">
          <h1 className="mb-2 font-bold font-serif text-3xl lg:text-4xl">
            {post?.body.tag?.tagTitle}
          </h1>
        </article>
        <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground text-sm">
          <div className="flex items-center">
            <User className="mr-1.5 h-4 w-4" />{" "}
            {post?.body.tag?.creater.userName}
          </div>
          <div className="flex items-center">
            <Clock className="mr-1.5 h-4 w-4" />{" "}
            {post?.body.tag?.createdAt.toLocaleDateString()}
          </div>
          <div className="flex items-center">
            <MessageSquare className="mr-1.5 h-4 w-4" /> {messages?.length || 0}
          </div>
          <div className="flex items-center">
            <Eye className="mr-1.5 h-4 w-4" />{" "}
            {/* TODO:{post?.body.tag?.views} */}
          </div>
        </div>

        {/* Ana Gönderi Kartı */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex flex-col md:grid md:grid-cols-[200px_1fr]">
              <div className="flex flex-row items-center border-b bg-muted/30 p-4 text-center md:flex-col md:border-r md:border-b-0">
                <Avatar className="relative h-16 w-16 md:h-28 md:w-28">
                  <AvatarImage
                    src={post?.body.tag?.creater.image || ""}
                    alt={post?.body.tag?.creater.userName}
                  />
                  <AvatarFallback className="text-3xl">
                    {post?.body.tag?.creater.userName
                      ?.charAt(0)
                      .toUpperCase() || "U"}
                  </AvatarFallback>
                  {/* TODO: {post?.body.tag?.creater.online && (
                    <span className="absolute right-0 bottom-0 block h-4 w-4 rounded-full border-2 border-card bg-green-500 md:h-5 md:w-5" />
                  )} */}
                </Avatar>
                <div className="ml-4 grow text-left md:mt-3 md:ml-0 md:text-center">
                  <h3 className="font-bold text-lg text-primary">
                    {post?.body.tag?.creater.userName}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {/*TODO:  {post?.body.tag?.creater.rank} */}
                    Üye
                  </p>
                </div>
              </div>

              <div className="flex flex-col p-4 sm:p-6">
                <div className="mb-4 flex items-center justify-between text-muted-foreground text-xs">
                  <span>{post?.body.tag?.createdAt.toLocaleDateString()}</span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        navigator.share({
                          title: post?.body.tag?.tagTitle,
                          url: window.location.href,
                        });
                      }}
                      title="Başlığı kopyala"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <span className="font-semibold text-foreground">
                      #{post?.body.tag?.tagId}
                    </span>
                    {/* TODO:spam */}
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <article className="wrap-break-word space-y-4 font-sans text-base">
                  {post?.body.tag?.tagContent.split("\n").map((line) => (
                    <p className="max-w-2xl">{line}</p>
                  ))}
                </article>

                <Separator className="my-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 space-y-6">
          {messages?.map((message) => (
            <Card
              key={message.id}
              id={`message-${message.id}`}
              className="overflow-hidden"
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:grid md:grid-cols-[200px_1fr]">
                  <div className="flex flex-row items-center border-b bg-muted/30 p-4 text-center md:flex-col md:border-r md:border-b-0">
                    <Avatar className="relative h-16 w-16 md:h-28 md:w-28">
                      <AvatarImage
                        src={message.sender.image || ""}
                        alt={message.sender.userName}
                      />
                      <AvatarFallback className="text-3xl">
                        {message.sender.userName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="ml-4 grow text-left md:mt-3 md:ml-0 md:text-center">
                      <h3 className="font-bold text-lg text-primary">
                        {message.sender.userName}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        Üye {/* TODO: message.sender.rank */}
                      </p>
                    </div>
                  </div>

                  {/* Cevap İçeriği (Sağ Taraf) */}
                  <div className="flex flex-col p-4 sm:p-6">
                    <div className="mb-4 flex items-center justify-between text-muted-foreground text-xs">
                      <span>
                        {new Date(message.createdAt).toLocaleDateString()}
                      </span>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <article className="wrap-break-word space-y-4 font-sans text-base ">
                      {(() => {
                        try {
                          const parsed = JSON.parse(message.messageData);
                          if (
                            parsed &&
                            typeof parsed === "object" &&
                            parsed.root
                          ) {
                            return (
                              <MessageArea editorSerializedState={parsed} />
                            );
                          }
                        } catch (_e) {
                          // console.error("JSON parsing error:", e);
                        }
                        return message.messageData
                          .split("\n")
                          .map((line, i) => (
                            <p className="max-w-2xl" key={i}>
                              {line}
                            </p>
                          ));
                      })()}
                    </article>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-6" id="reply-form">
          <Card className="mt-6" id="reply-form">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start space-x-4">
                <Avatar>
                  <AvatarImage
                    src={user?.image || ""}
                    alt={user?.userName || undefined}
                  />
                  <AvatarFallback>
                    {user?.userName
                      ? user.userName.charAt(0).toUpperCase()
                      : "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex w-full max-w-10/12 flex-col md:max-w-3xl lg:max-w-4xl">
                  <ReplyEditor tagId={tagId} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
